import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Button,
  Checkbox,
  Chip as _Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import CreateIssue from '../../../../entity/CreateIssue';
import CreateIssueComment from '../../../../entity/CreateIssueComment';
import UploadingFile from '../../../../entity/UploadingFile';
import UploadingFilesProgress from '../../../../entity/UploadingFilesProgress';
import { DeliverableForCraetionDetailFragment, Issue, IssueComment } from '../../../../generated/graphql';
import { toJSDate } from '../../../../graphql/datetime';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import LfToBr from '../../primitive/LfToBr';
import AvatarAndName from '../AvatarAndName';
import FileList, { FileListItem, FileListUploadingItem } from '../FileList';
import _FileUploader from '../FileUploader';

interface Props {
  deliverable?: DeliverableForCraetionDetailFragment | null;
  bucketId: string | null;
  issue: Pick<Issue, 'id' | 'resolved'> & {
    comments: Array<
      Pick<IssueComment, 'assetIds' | 'createdAt' | 'body'> & {
        user: { name?: string; profileImageUrl?: string };
      }
    >;
  };
  files: Array<FileReference>;
  createIssue: CreateIssue;
  createIssueComment: CreateIssueComment;
  modificationRequestApproved: boolean;
  modificationRequestExpired: boolean;
  onUploadIssueCommentAssetFiles: (files: UploadingFile[], deliverableId: string, issueId: string) => void;
  onRemoveAssetIssueCommentFile: (bucketId: string, fileId: string, issueId: string) => void;
  onSubmitModificationRequestIssueComment: (issueId: string, comment: string) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  className?: string;
}

const IssueDetail: FC<Props> = ({
  deliverable,
  bucketId,
  issue,
  files,
  createIssue,
  createIssueComment,
  modificationRequestApproved,
  modificationRequestExpired,
  onUploadIssueCommentAssetFiles,
  onRemoveAssetIssueCommentFile,
  onSubmitModificationRequestIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  className,
}) => {
  const [inputComment, commentHandler] = useInputComment('');

  const {
    assetIds,
    uploadingAssetFiles,
    uploadingFilesProgress,
  }: {
    assetIds: Array<string>;
    uploadingAssetFiles: UploadingFile[];
    uploadingFilesProgress: UploadingFilesProgress;
  } = createIssueComment ? createIssueComment : createIssue;

  const uploadedFiles = files.filter((file) => assetIds.includes(file.id));

  return (
    <Root className={className} id={issue.id}>
      {modificationRequestExpired ? null : (
        <Head>
          <Titles>
            {issue.resolved ? (
              <Chip label={<FormattedMessage id="modification_request.detail_card.issue.closed" />} />
            ) : (
              <IssueOpend label={<FormattedMessage id="modification_request.detail_card.issue.open" />} />
            )}

            <Title variant="body1">
              <FormattedMessage id="modification_request.detail_card.issue.title" />
            </Title>
          </Titles>
          <FormControlLabel
            control={
              <Checkbox
                checked={issue.resolved}
                onChange={() =>
                  issue.resolved
                    ? onRequestReopenModificationRequestIssue(issue.id)
                    : onRequestResolveModificationRequestIssue(issue.id)
                }
                disabled={modificationRequestApproved}
              />
            }
            label={<FormattedMessage id="modification_request.detail_card.issue.check" />}
          />
        </Head>
      )}

      {issue.comments.map((comment, index) => {
        const fileItems = files.filter((file) => comment.assetIds.includes(file.id));

        return (
          <IssueItem key={`issue-item-${index}`}>
            <User user={comment.user} size={32} />

            <InputtedComment variant="subtitle1" component="p">
              <LfToBr text={comment.body} />
            </InputtedComment>

            {fileItems ? (
              <FileList>
                {[
                  ...fileItems.map((file) => (
                    <FileListItem file={file} key={`issue-file-${file.id}`} isVisibleSecondary={false} />
                  )),
                ]}
              </FileList>
            ) : null}

            <Typography variant="body2" color="textSecondary">
              <FormattedDate
                value={toJSDate(comment.createdAt)}
                year="numeric"
                month="2-digit"
                day="2-digit"
                hour="2-digit"
                minute="2-digit"
              />
            </Typography>
          </IssueItem>
        );
      })}

      {modificationRequestApproved || modificationRequestExpired ? null : (
        <FormArea>
          <CommentForm error={inputComment.error}>
            <Comment
              label={<FormattedMessage id="modification_request.detail_card.input_comment.label" />}
              value={inputComment.body}
              variant="outlined"
              rows="4"
              rowsMax="8"
              multiline
              error={inputComment.error}
              onChange={(e) => commentHandler.setComment(e.target.value)}
            />
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>

          {(bucketId && uploadedFiles.length > 0) || uploadingAssetFiles.length > 0 ? (
            <FileList>
              {[
                ...uploadedFiles.map((file) => (
                  <FileListItem
                    file={file}
                    key={`uploaded-file-${file.id}`}
                    isVisibleSecondary={false}
                    onClickDeleteButton={() => onRemoveAssetIssueCommentFile(bucketId!, file.id, issue.id)}
                  />
                )),
                ...uploadingAssetFiles.map((file) => {
                  const targetProgressFile = uploadingFilesProgress[file.id];

                  return (
                    <FileListUploadingItem
                      file={file}
                      key={`uploading-file-${file.id}`}
                      progress={targetProgressFile ? targetProgressFile.progress : 0}
                      state="uploading"
                    />
                  );
                }),
              ]}
            </FileList>
          ) : null}

          {deliverable && bucketId ? (
            <FileUploader
              key={`uploader-${issue.id}`}
              onSelectFiles={(_, files) => {
                onUploadIssueCommentAssetFiles(files, deliverable.id, issue.id);
              }}
            />
          ) : null}

          <Buttons>
            <Submit
              variant="contained"
              color="primary"
              size="small"
              disabled={inputComment.body.trim() === '' || inputComment.error || uploadingAssetFiles.length > 0}
              onClick={() => {
                commentHandler.setComment('');
                onSubmitModificationRequestIssueComment(issue.id, inputComment.body);
              }}
            >
              <FormattedMessage id="modification_request.detail_card.issue.submit" />
            </Submit>
          </Buttons>
        </FormArea>
      )}
    </Root>
  );
};

const Root = styled('div')({});

const Head = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  {
    marginBottom: SPACING_UNIT * 3,
  },
);

const Titles = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }));

const Title = styled(Typography)({
  fontWeight: 700,
});

const Chip = styled(_Chip)({
  marginRight: SPACING_UNIT,
});

const IssueOpend = styled(Chip)({
  marginRight: SPACING_UNIT,
  background: theme.palette.error.main,
  color: '#fff',
});

const IssueItem = styled('div')({
  marginBottom: SPACING_REGULAR,
});

const User = styled(AvatarAndName)({
  marginBottom: SPACING_UNIT,
});

const InputtedComment = (styled(Typography)({
  wordBreak: 'break-word',
  hyphens: 'auto',
}) as unknown) as typeof Typography;

const FormArea = styled('div')({
  width: '100%',
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_REGULAR,
});

const Comment = styled(TextField)({
  width: '100%',
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const FileUploader = styled(_FileUploader)({
  padding: SPACING_REGULAR,
});

const Buttons = styled('div')({
  textAlign: 'right',
});

const Submit = styled(Button)({
  marginTop: SPACING_REGULAR,
  width: 'auto',
});

export default IssueDetail;
